import {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {DisplayMessage, WeddingOccasionDB} from '../lib/interfaces';
import {getNearestDatesForEachDayInMonth, getUniqueListOfMonthsFromDates} from '../helpers';
import {t} from 'i18next';
import AvailableTimes from '../components/AvailableTimes';
import {useBookingState} from '../store';
import {BreakpointMinWidth, DisplayMessageTypes, RoutePath} from '../lib/enums';
import {useUserClaims} from '../hooks/useUserClaims';
import AbortBooking from '../components/AbortBooking';
import LoginButton from '../components/LoginButton';
import {useWindowSize} from '@uidotdev/usehooks';
import {HandleRebookButton} from '../components/HandleRebookButton';
import {getWeddingOccasions} from '../services/weddingOccasionServices';
import {Trans} from 'react-i18next';
import {UPPSALA_KOMMUN_PHONE_NUMBER} from '../utils/constants';
import {UaButton, UaDisplayMessage} from 'uppsala.ui.react';

interface SelectTimePageProps {
  inRebookMode?: boolean;
}

const SelectTimePage = (props: SelectTimePageProps) => {
  const {userClaims} = useUserClaims();
  const state = useBookingState();
  const navigate = useNavigate();
  const size = useWindowSize();

  const {bookingId} = useParams<{bookingId: string}>();

  const [availableOccasions, setAvailableOccasions] = useState<WeddingOccasionDB[]>([]);
  const [availableDateMonths, setAvailableDateMonths] = useState<string[]>([]);
  const [weddingOccasions, setWeddingOccasions] = useState<WeddingOccasionDB[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [displayMessage, setDisplayMessage] = useState<DisplayMessage | null>(null);
  const [rebookingCompleted, setRebookingCompleted] = useState(false);

  useEffect(() => {
    fetchData();
    if (state.isCustomOccasion) state.clearState();
    const fakeLoggedIn = sessionStorage.getItem('fakeLoggedIn');
    setIsLoggedIn(userClaims !== null || fakeLoggedIn !== null);
  }, [userClaims]);

  // Wait for booking state to be set before showing occasions for the selected month
  useEffect(() => {
    if (state?.selectedMonthYear?.length < 0) return;
    setAvailableOccasionsForMonth(state?.selectedMonthYear);
  }, [weddingOccasions.length > 0]);

  function onOccasionSelected(weddingOccasionId: string) {
    const wo = availableOccasions.find((item) => item.id === weddingOccasionId);
    if (!wo) return state.setWeddingOccasionId('');
    state.setWeddingOccasion(wo);
  }

  async function fetchData() {
    const result = await getWeddingOccasions();
    if (result.data) {
      const availableDateMonths = getUniqueListOfMonthsFromDates(result.data.map((item) => item.time));
      setWeddingOccasions(result.data);
      setAvailableDateMonths(availableDateMonths);
      setDisplayMessage(null);
    } else {
      setDisplayMessage({
        type: result.info?.type,
        message: (
          <p className="whitespace-pre-line">
            <Trans
              i18nKey={'apiResponse.' + result.info?.message}
              values={{number: UPPSALA_KOMMUN_PHONE_NUMBER}}
              components={{
                1: <a href={`tel:${UPPSALA_KOMMUN_PHONE_NUMBER}`} aria-label="Telefonnummer" />,
              }}
            />
          </p>
        ),
      } as DisplayMessage);
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }

  function setAvailableOccasionsForMonth(dateString: string) {
    if (!dateString) return;
    const yearMonthFormat = dateString.slice(0, 7); // "2024-03"

    // Filter out all occasions that are not in the selected month
    const filteredOccasions = weddingOccasions.filter((item) => item.time.includes(yearMonthFormat));

    // Get the nearest date for each day in the selected month
    const dateStrings = filteredOccasions.map((item) => item.time.toString());
    const nearestDates = getNearestDatesForEachDayInMonth(dateStrings, dateString);

    // Filter out all occasions that are not in the nearest dates
    // TODO: This is a workaround for the backend not returning the correct dates. Should be moved to the backend later.
    const availableOccasions = filteredOccasions.filter((item) => {
      const date = new Date(item.time);
      date.setUTCHours(date.getUTCHours() - date.getTimezoneOffset() / 60);
      date.setUTCMilliseconds(0); // set milliseconds to zero
      const dateString = date.toISOString();
      return nearestDates.includes(dateString);
    });

    setAvailableOccasions(availableOccasions);
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      <h1>{props.inRebookMode ? t('Välj ny tid för vigsel') : t('booking.header.bookBorgerligVigsel')}</h1>
      {!rebookingCompleted && (
        <>
          <AvailableTimes
            key="book-time"
            bookingState={state}
            loading={isLoading}
            availableDateMonths={availableDateMonths}
            availableOccasions={availableOccasions}
            onSelectedOccasionId={onOccasionSelected}
            onMonthYearSelected={(newDateString) => {
              setAvailableOccasionsForMonth(newDateString);
              state.setSelectedMonthYear(newDateString);
            }}
          />
          <div style={{marginTop: 'auto'}} />
        </>
      )}

      {displayMessage && (
        <UaDisplayMessage
          style={{maxWidth: '550px', marginTop: '25px'}}
          type={displayMessage.type}
          message={displayMessage.message}
        />
      )}

      {state?.weddingOccasionId?.length > 0 && !isLoading ? (
        <div>
          {!isLoggedIn && <label htmlFor="continueBookTimeButton">{t('booking.loginIsRequired')}</label>}

          <div
            className="flex-row"
            style={{
              marginTop: '15px',
              gap: '15px',
              flexWrap: size.width && size.width < BreakpointMinWidth.xs ? 'wrap-reverse' : 'nowrap',
            }}>
            {props.inRebookMode ? (
              <HandleRebookButton
                bookingId={bookingId}
                targetWeddingOccasionId={state.weddingOccasionId}
                onResult={(dm) => {
                  setRebookingCompleted(dm.type === DisplayMessageTypes.Success);
                  setDisplayMessage(dm);
                }}
              />
            ) : (
              <>
                <AbortBooking />
                {isLoggedIn ? (
                  <UaButton
                    type="primary"
                    id="continueBookTimeButton"
                    title={t('button.book')}
                    onClick={() => {
                      navigate(RoutePath.ContactInfoPage);
                    }}
                  />
                ) : (
                  <LoginButton title={t('button.book')} returnUrl={RoutePath.ContactInfoPage} />
                )}
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SelectTimePage;
